import { DateTime } from 'luxon';
import type { Contact } from '../types/contacts';
import type { ContactFormValues } from '../types/formSubmissions';
import { getContactName, getSigningName } from './contactNames';

type ContactProfileField = keyof Pick<
    Contact,
    | 'firstIdType'
    | 'secondIdType'
    | 'firstIdFront'
    | 'firstIdBack'
    | 'firstIdSingle'
    | 'secondIdFront'
    | 'secondIdBack'
    | 'secondIdSingle'
    | 'homePhone'
    | 'homeAddress'
    | 'homeUnit'
    | 'dateOfBirth'
    | 'gender'
    | 'preferredPronouns'
    | 'maritalStatus'
    | 'citizenshipStatus'
    | 'occupation'
    | 'employer'
    | 'employerAddress'
    | 'employerUnit'
    | 'employerPhone'
>;

const CONTACT_PROFILE_FIELD_MAP = {
    firstIdType: 'firstIdType',
    secondIdType: 'secondIdType',
    firstIdFront: 'firstIdFront',
    firstIdBack: 'firstIdBack',
    firstIdSingle: 'firstIdSingle',
    secondIdFront: 'secondIdFront',
    secondIdBack: 'secondIdBack',
    secondIdSingle: 'secondIdSingle',
    homePhone: 'homePhone',
    homeAddress: 'homeAddress',
    homeUnit: 'homeUnit',
    dateOfBirth: 'dateOfBirth',
    gender: 'gender',
    preferredPronouns: 'preferredPronouns',
    maritalStatus: 'maritalStatus',
    citizenshipStatus: 'citizenshipStatus',
    occupation: 'occupation',
    employer: 'employer',
    employerAddress: 'employerAddress',
    employerUnit: 'employerUnit',
    employerPhone: 'employerPhone',
} as const satisfies Record<ContactProfileField, keyof ContactFormValues>;

/** Stored contacts columns derived from form values (excludes the generated name columns). */
export interface ContactFieldsFromFormValues {
    firstName: string;
    middleName: string | null;
    lastName: string;
    contactEmail: string;
    phoneNumber: string | null;
    firstIdType: string | null;
    secondIdType: string | null;
    firstIdFront: string | null;
    firstIdBack: string | null;
    firstIdSingle: string | null;
    secondIdFront: string | null;
    secondIdBack: string | null;
    secondIdSingle: string | null;
    homePhone: string | null;
    homeAddress: string | null;
    homeUnit: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    preferredPronouns: string | null;
    maritalStatus: string | null;
    citizenshipStatus: string | null;
    occupation: string | null;
    employer: string | null;
    employerAddress: string | null;
    employerUnit: string | null;
    employerPhone: string | null;
}

export interface ContactProfileFromFormValues extends ContactFieldsFromFormValues {
    contactName: string;
    signingName: string;
}

function optionalContactField(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

function optionalFormField(value: string | null | undefined): string | undefined {
    return optionalContactField(value) ?? undefined;
}

function mapFormValuesToProfileFields(
    formValues: ContactFormValues,
): Pick<ContactProfileFromFormValues, ContactProfileField> {
    return Object.fromEntries(
        Object.entries(CONTACT_PROFILE_FIELD_MAP).map(([contactField, formField]) => [
            contactField,
            contactField === 'dateOfBirth'
                ? normalizeDateOfBirth(formValues[formField])
                : optionalContactField(formValues[formField]),
        ]),
    ) as Pick<ContactProfileFromFormValues, ContactProfileField>;
}

export interface LegacyUserContactDetailProfileSource {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    maritalStatus?: string | null;
    citizenshipStatus?: string | null;
    dateOfBirth?: Date | string | null;
    gender?: string | null;
    preferredPronouns?: string | null;
    occupation?: string | null;
    employer?: string | null;
    employerAddress?: string | null;
    employerPhoneNumber?: string | null;
    employerUnitNumber?: string | null;
    address?: string | null;
    unitNumber?: string | null;
}

/**
 * How V2 intakes stored dates of birth before they were stored as ISO dates, e.g. "Jan 31 1980".
 * Remove once the backfill in docs/contact-data-model.md ("Date of birth format") has run.
 */
const LEGACY_DATE_OF_BIRTH_FORMAT = 'MMM dd yyyy';

/**
 * Reads a date of birth in any stored form: an ISO date or timestamp, the legacy V2 format
 * ("Jan 31 1980"), or a Date from V1's timestamp column (read as UTC, which is how it is stored).
 * Returns null when empty, and an invalid DateTime when the value can't be parsed.
 */
export function parseDateOfBirth(value: Date | string | null | undefined): DateTime | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return DateTime.fromJSDate(value, { zone: 'utc' });
    }

    const trimmed = value.trim();
    if (trimmed === '') {
        return null;
    }

    const iso = DateTime.fromISO(trimmed, { setZone: true });
    if (iso.isValid) {
        return iso;
    }

    // Month abbreviations depend on locale; the legacy values were always written in English
    return DateTime.fromFormat(trimmed, LEGACY_DATE_OF_BIRTH_FORMAT, { locale: 'en-US' });
}

/** Dates of birth are stored as ISO dates (yyyy-MM-dd). Null when empty or unparseable. */
export function normalizeDateOfBirth(value: Date | string | null | undefined): string | null {
    const parsed = parseDateOfBirth(value);
    return parsed?.isValid ? parsed.toISODate() : null;
}

export function legacyUserContactDetailToContactProfile(
    source: LegacyUserContactDetailProfileSource,
): Partial<
    Pick<
        ContactProfileFromFormValues,
        ContactProfileField | 'homeAddress' | 'homeUnit' | 'dateOfBirth'
    >
> {
    const profile = {
        homeAddress: optionalContactField(source.address),
        homeUnit: optionalContactField(source.unitNumber),
        dateOfBirth: normalizeDateOfBirth(source.dateOfBirth),
        gender: optionalContactField(source.gender),
        preferredPronouns: optionalContactField(source.preferredPronouns),
        maritalStatus: optionalContactField(source.maritalStatus),
        citizenshipStatus: optionalContactField(source.citizenshipStatus),
        occupation: optionalContactField(source.occupation),
        employer: optionalContactField(source.employer),
        employerAddress: optionalContactField(source.employerAddress),
        employerUnit: optionalContactField(source.employerUnitNumber),
        employerPhone: optionalContactField(source.employerPhoneNumber),
    };

    return Object.fromEntries(
        Object.entries(profile).filter(([, value]) => value !== null),
    ) as Partial<
        Pick<
            ContactProfileFromFormValues,
            ContactProfileField | 'homeAddress' | 'homeUnit' | 'dateOfBirth'
        >
    >;
}

/** Fields to write to a contacts row. contact_name and signing_name are generated by the database. */
export function contactFormValuesToContactFields(
    formValues: ContactFormValues,
): ContactFieldsFromFormValues {
    return {
        firstName: formValues.firstName.trim(),
        middleName: optionalContactField(formValues.middleName),
        lastName: formValues.lastName.trim(),
        contactEmail: formValues.email.trim().toLowerCase(),
        phoneNumber: optionalContactField(formValues.cellPhone),
        ...mapFormValuesToProfileFields(formValues),
    };
}

export function contactFormValuesToContactProfile(
    formValues: ContactFormValues,
): ContactProfileFromFormValues {
    const fields = contactFormValuesToContactFields(formValues);

    return {
        ...fields,
        contactName: getContactName(fields),
        signingName: getSigningName(fields),
    };
}

export function contactToContactFormValues(
    contact: Pick<
        Contact,
        | 'firstName'
        | 'middleName'
        | 'lastName'
        | 'contactEmail'
        | 'phoneNumber'
        | ContactProfileField
    >,
): ContactFormValues {
    const formValues: ContactFormValues = {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.contactEmail,
        middleName: optionalFormField(contact.middleName),
        cellPhone: optionalFormField(contact.phoneNumber),
    };

    for (const [contactField, formField] of Object.entries(CONTACT_PROFILE_FIELD_MAP)) {
        const fieldValue = contact[contactField as ContactProfileField];
        const value =
            contactField === 'dateOfBirth'
                ? (normalizeDateOfBirth(fieldValue) ?? undefined)
                : optionalFormField(fieldValue ?? undefined);
        if (value !== undefined) {
            formValues[formField] = value;
        }
    }

    return formValues;
}

export function overlayContactFormValuesWithContact(
    formValues: ContactFormValues,
    contact: Pick<
        Contact,
        | 'firstName'
        | 'middleName'
        | 'lastName'
        | 'contactEmail'
        | 'phoneNumber'
        | ContactProfileField
    >,
): ContactFormValues {
    const overlaid = contactToContactFormValues(contact);

    return {
        ...formValues,
        firstName: getOverlayValue(contact.firstName, formValues.firstName),
        middleName: getOverlayValue(contact.middleName, formValues.middleName),
        lastName: getOverlayValue(contact.lastName, formValues.lastName),
        email: getOverlayValue(contact.contactEmail, formValues.email),
        cellPhone: getOverlayValue(contact.phoneNumber, formValues.cellPhone),
        firstIdType: getOverlayValue(overlaid.firstIdType, formValues.firstIdType),
        secondIdType: getOverlayValue(overlaid.secondIdType, formValues.secondIdType),
        firstIdFront: getOverlayValue(overlaid.firstIdFront, formValues.firstIdFront),
        firstIdBack: getOverlayValue(overlaid.firstIdBack, formValues.firstIdBack),
        firstIdSingle: getOverlayValue(overlaid.firstIdSingle, formValues.firstIdSingle),
        secondIdFront: getOverlayValue(overlaid.secondIdFront, formValues.secondIdFront),
        secondIdBack: getOverlayValue(overlaid.secondIdBack, formValues.secondIdBack),
        secondIdSingle: getOverlayValue(overlaid.secondIdSingle, formValues.secondIdSingle),
        homePhone: getOverlayValue(overlaid.homePhone, formValues.homePhone),
        homeAddress: getOverlayValue(overlaid.homeAddress, formValues.homeAddress),
        homeUnit: getOverlayValue(overlaid.homeUnit, formValues.homeUnit),
        dateOfBirth: getOverlayValue(overlaid.dateOfBirth, formValues.dateOfBirth),
        gender: getOverlayValue(overlaid.gender, formValues.gender),
        preferredPronouns: getOverlayValue(
            overlaid.preferredPronouns,
            formValues.preferredPronouns,
        ),
        maritalStatus: getOverlayValue(overlaid.maritalStatus, formValues.maritalStatus),
        citizenshipStatus: getOverlayValue(
            overlaid.citizenshipStatus,
            formValues.citizenshipStatus,
        ),
        occupation: getOverlayValue(overlaid.occupation, formValues.occupation),
        employer: getOverlayValue(overlaid.employer, formValues.employer),
        employerAddress: getOverlayValue(overlaid.employerAddress, formValues.employerAddress),
        employerUnit: getOverlayValue(overlaid.employerUnit, formValues.employerUnit),
        employerPhone: getOverlayValue(overlaid.employerPhone, formValues.employerPhone),
    };
}

function getOverlayValue<T extends string | null | undefined>(
    freshValue: string | null | undefined,
    storedValue: T,
): T {
    return freshValue && freshValue.trim() !== '' ? (freshValue as T) : storedValue;
}
